import config from '../config/environment';
import { clearSession, getSessionRevision, getSessionToken, isTransientSessionError } from './session';

export class ApiError extends Error {
  constructor(message, status, payload = null) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

// Store reference to refresh token function (will be set by AuthContext)
let refreshTokenFn = null;

export const setRefreshTokenFunction = (fn) => {
  refreshTokenFn = fn;
};

// API Client for handling all HTTP calls
class ApiClient {
  constructor(baseURL = config.apiBaseUrl) {
    this.baseURL = baseURL;
    this.investorUploads = new Set();
  }

  // Helper method to build full URL
  buildUrl(endpoint) {
    // In development, use relative URLs to work with proxy
    // In production, use the configured baseURL for Azure backend
    if (process.env.NODE_ENV === 'development' && !this.baseURL) {
      return endpoint;
    }
    return this.baseURL ? `${this.baseURL}${endpoint}` : endpoint;
  }

  // Helper method for common request options
  getRequestOptions(method = 'GET', body = null) {
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // Add authentication token if available
    const token = getSessionToken();
    if (token) {
      options.headers['Authorization'] = `Bearer ${token}`;
    }

    if (body) {
      options.body = JSON.stringify(body);
    }

    return options;
  }

  // Generic request method with automatic token refresh on 401
  async request(endpoint, options = {}, retryCount = 0) {
    const { sessionIndependent = false, ...fetchOptions } = options;
    try {
      const url = this.buildUrl(endpoint);
      
      // Get auth token
      const token = getSessionToken();
      const sessionRevision = getSessionRevision();
      
      // Merge auth headers with any existing headers
      const headers = {
        ...options.headers,
      };
      
      // Don't add auth header for public authentication endpoints
      const isAuthEndpoint = endpoint === '/api/auth/google/login' ||
                            endpoint.includes('/auth/login') ||
                            endpoint.includes('/auth/verify') ||
                            endpoint.includes('/auth/refresh') ||
                            endpoint.includes('/auth/logout');
      
      if (token && !isAuthEndpoint && !sessionIndependent) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Merge options with auth headers
      const requestOptions = {
        ...fetchOptions,
        headers,
      };
      
      const response = await fetch(url, requestOptions);
      if (!isAuthEndpoint && !sessionIndependent && sessionRevision !== getSessionRevision()) {
        throw new DOMException('Session changed.', 'AbortError');
      }
      
      // Handle 401 Unauthorized - token might be expired
      const readOnly = ['GET', 'HEAD'].includes((options.method || 'GET').toUpperCase());
      if (response.status === 401 && !isAuthEndpoint && !sessionIndependent && retryCount === 0 && readOnly && token && refreshTokenFn) {
        try {
          const newToken = await refreshTokenFn();
          if (sessionRevision !== getSessionRevision() || options.signal?.aborted) throw new DOMException('Session changed or request cancelled.', 'AbortError');
          if (!newToken) throw new Error('Session refresh failed. Please sign in again.');
        } catch (refreshError) {
          if (sessionRevision === getSessionRevision() && refreshError.name !== 'AbortError' && !isTransientSessionError(refreshError)) clearSession('Your session expired. Please sign in again.');
          throw refreshError;
        }
        return this.request(endpoint, options, retryCount + 1);
      }
      
      if (!response.ok) {
        const text = await response.text();
        let message = text;
        let payload = null;
        if (text) {
          try {
            payload = JSON.parse(text);
            message = payload?.error || payload?.message || `Request failed (${response.status}).`;
          } catch {
            message = `Request failed (${response.status}).`;
          }
        }
        if (response.status === 401 && !isAuthEndpoint && !sessionIndependent && sessionRevision === getSessionRevision()) clearSession('Your session expired. Please sign in again.');
        if (response.status === 403) message = message || 'This account is inactive or does not have permission.';
        throw new ApiError(message || `Request failed (${response.status}).`, response.status, payload);
      }
      if (response.status === 204) return null;
      let payload;
      if (options.asBlob || options.responseType === 'blob') {
        payload = await response.blob();
      } else {
        try {
          payload = await response.json();
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          throw new ApiError('The API returned a non-JSON response. Check the backend deployment and proxy.', response.status);
        }
      }
      if (!isAuthEndpoint && !sessionIndependent && sessionRevision !== getSessionRevision()) throw new DOMException('Session changed.', 'AbortError');
      return payload;
    } catch (error) {
      if (error.name !== 'AbortError') console.error('API request failed:', options.method || 'GET', endpoint.split('?')[0], error.status || 'network', error.message);
      throw error;
    }
  }

  buildQuery(params = {}) {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
    const value = query.toString();
    return value ? `?${value}` : '';
  }

  async getInvestors(status, options) { return this.request(`/api/investors${this.buildQuery({ status })}`, options); }
  async getInvestor(investorId, options) { return this.request(`/api/investors/${encodeURIComponent(investorId)}`, options); }
  async createInvestor(investor) { return this.request('/api/investors', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(investor) }); }
  async updateInvestor(investorId, investor) { return this.request(`/api/investors/${encodeURIComponent(investorId)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(investor) }); }
  async deleteInvestor(investorId) { return this.request(`/api/investors/${encodeURIComponent(investorId)}`, { method: 'DELETE' }); }
  async getInvestorVehicles(investorId, options) { return this.request(`/api/investors/${encodeURIComponent(investorId)}/vehicles`, options); }
  async assignVehicleOwner(vehicleId, { investor_id }) { return this.request(`/api/vehicle-profiles/${encodeURIComponent(vehicleId)}/owner`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ investor_id }) }); }
  async releaseVehicleOwner(vehicleId) { return this.request(`/api/vehicle-profiles/${encodeURIComponent(vehicleId)}/owner`, { method: 'DELETE' }); }
  async importFinanceWorkbook({ file, period, dryRun = false, replaceExisting = false }) {
    const formData = new FormData();
    formData.append('period', period);
    formData.append('dry_run', String(dryRun));
    formData.append('replace_existing', String(replaceExisting));
    formData.append('file', file);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120000);
    try {
      return await this.request('/api/finance/imports', { method: 'POST', body: formData, signal: controller.signal });
    } finally { clearTimeout(timeout); }
  }
  async importInvestorReport({ investorId, file, from, to, dryRun = false }) {
    if (this.investorUploads.has(investorId)) {
      throw new Error('An upload for this investor is already processing. Wait for its result before submitting another.');
    }
    this.investorUploads.add(investorId);
    const formData = new FormData();
    formData.append('investor_id', investorId);
    formData.append('file', file);
    formData.append('from', from);
    formData.append('to', to);
    formData.append('dry_run', String(dryRun));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120000);
    try {
      return await this.request('/api/finance/investor-imports', { method: 'POST', body: formData, signal: controller.signal });
    } finally {
      clearTimeout(timeout);
      this.investorUploads.delete(investorId);
    }
  }
  async getFinanceImports(filters = {}, options) { return this.request(`/api/finance/imports${this.buildQuery(filters)}`, options); }
  async getFinanceImport(importId, period, options) { return this.request(`/api/finance/imports/${encodeURIComponent(importId)}${this.buildQuery({ period })}`, options); }
  async getOwnerOverview(filters = {}, options) { return this.request(`/api/owner/overview${this.buildQuery(filters)}`, options); }
  async getOwnerVehicles(filters = {}, options) { return this.request(`/api/owner/vehicles${this.buildQuery(filters)}`, options); }
  async getOwnerVehicle(vehicleId, filters = {}, options) { return this.request(`/api/owner/vehicles/${encodeURIComponent(vehicleId)}${this.buildQuery(filters)}`, options); }
  async getOwnerVehicleStatements(vehicleId, filters = {}, options) { return this.request(`/api/owner/vehicles/${encodeURIComponent(vehicleId)}/statements${this.buildQuery(filters)}`, options); }
  async getOwnerStatements(filters = {}, options) { return this.request(`/api/owner/statements${this.buildQuery(filters)}`, options); }
  async getOwnerSummary(filters = {}, options) { return this.request(`/api/owner/summary${this.buildQuery(filters)}`, options); }
  async listVehicleProfiles(filters = {}, options) { return this.request(`/api/vehicle-profiles${this.buildQuery(filters)}`, options); }
  async getVehicleProfile(profileId, options) { return this.request(`/api/vehicle-profiles/${encodeURIComponent(profileId)}`, options); }
  async createVehicleProfile(profile) {
    return this.request('/api/vehicle-profiles', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(profile) });
  }
  async patchVehicleProfile(profileId, profile) {
    return this.request(`/api/vehicle-profiles/${encodeURIComponent(profileId)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(profile) });
  }
  async mergeVehicleProfiles(payload) {
    return this.request('/api/vehicle-profiles/merge', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  }
  async getVehicleCosts(profileId, options) { return this.request(`/api/vehicle-profiles/${encodeURIComponent(profileId)}/costs`, options); }
  async createVehicleCost(profileId, cost) {
    return this.request(`/api/vehicle-profiles/${encodeURIComponent(profileId)}/costs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cost) });
  }
  async updateVehicleCost(profileId, costId, cost, etag) {
    return this.request(`/api/vehicle-profiles/${encodeURIComponent(profileId)}/costs/${encodeURIComponent(costId)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'If-Match': etag }, body: JSON.stringify(cost),
    });
  }
  async deleteVehicleCost(profileId, costId, etag) {
    return this.request(`/api/vehicle-profiles/${encodeURIComponent(profileId)}/costs/${encodeURIComponent(costId)}`, {
      method: 'DELETE', headers: { 'If-Match': etag },
    });
  }

  // Integration-related API calls
  async connectIntegration(providerName) {
    try {
      const url = this.buildUrl(`/api/integrations/${providerName}/connect?authType=oauth2`);
      
      const urlObj = new URL(url);
      console.log('Making request to:', url);

      // urlObj.searchParams.append('reject_cors_preflight', 'true');

      const response = await fetch(urlObj, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/plain; charset=dropbox-cors-hack',
        },
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (response.status === 302) {
        // Handle redirect
        const redirectUrl = response.headers.get('Location');
        if (redirectUrl) {
          return { redirectUrl, status: 302 };
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }
  
    async getIntegrations() {
      return this.request('/api/integrations');
    }
  
    async getIntegrationById(id) {
      return this.request(`/api/integrations/${id}`);
    }
  
    async disconnectIntegration(integrationId) {
      return this.request(`/api/integrations/${integrationId}/disconnect`, {
        method: 'POST',
      });
    }
  
    async updateIntegrationStatus(integrationId, status) {
      return this.request(`/api/integrations/${integrationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
    }
  
    // User-related API calls
    async getUserProfile() {
      return this.request('/api/user/profile');
    }
  
    async updateUserProfile(profileData) {
      return this.request('/api/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });
    }
  
    // Settings-related API calls
    async getSettings() {
      return this.request('/api/settings');
    }
  
    async updateSettings(settingsData) {
      return this.request('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsData),
      });
    }
  
    // Workflow-related API calls
    async getWorkflows() {
      return this.request('/api/workflows');
    }
  
    async createWorkflow(workflowData) {
      return this.request('/api/workflows', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workflowData),
      });
    }

    // Step 1: Initiate upload session to get SAS URLs
    async initiateUpload(count, uploaderId, picturesMetadata = [], expiryHours = 1, vehicleProfileId) {
      const payload = {
        count: count,
        uploader_id: uploaderId,
        expiry_hours: expiryHours,
        pictures_metadata: picturesMetadata,
        ...(vehicleProfileId ? { vehicle_profile_id: vehicleProfileId } : {}),
      };
      
      console.log('Initiating upload with payload:', payload);
      
      return this.request('/api/pictures/initiate-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    }

    // Step 2: Upload file directly to Azure Storage using SAS URL
    async uploadToSasUrl(sasUrl, file, onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        if (onProgress) {
          xhr.upload.addEventListener('progress', onProgress);
        }

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ success: true, status: xhr.status });
          } else {
            reject(new Error(`Upload to Azure failed with status: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during Azure upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Azure upload aborted'));
        });

        // Azure Blob Storage expects PUT request with the file as body
        xhr.open('PUT', sasUrl);
        
        // Set required Azure headers
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
        
        // Optional: Set content length (helps with upload)        
        xhr.send(file);
      });
    }

    // Step 3: Confirm upload status with session_id
    async confirmUpload(sessionId, status, vehicleProfileId) {
      return this.request('/api/pictures/confirm-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          status: status,
          ...(vehicleProfileId ? { vehicle_profile_id: vehicleProfileId } : {}),
        }),
      });
    }

    async confirmPhotoUpload(sessionId, vehicleProfileId) {
      try {
        const response = await this.confirmUpload(sessionId, 'success', vehicleProfileId);
        if (response?.status !== 'completed' || response.session_id !== sessionId ||
          response.reservation?.id !== sessionId || typeof response.reservation.vehicle_id !== 'string' ||
          response.vehicle_profile?.id !== response.reservation.vehicle_id ||
          (vehicleProfileId && response.reservation.vehicle_id !== vehicleProfileId) ||
          !Array.isArray(response.reservation.picture_ids) || response.reservation.picture_ids.length !== 4 ||
          !response.reservation.picture_ids.every((id) => typeof id === 'string')) {
          throw new Error('Upload confirmation could not be verified. Review or re-confirm the same session before uploading again.');
        }
        return response;
      } catch (error) {
        error.uploadSession = { session_id: sessionId, vehicle_profile_id: vehicleProfileId };
        throw error;
      }
    }

    // Each confirmed batch is one four-photo reservation.
    async uploadPhotoBatch(filesData, userId, vehicleProfileId) {
      // filesData is an array of objects: [{ file, metadata, onProgress }, ...]
      const count = filesData.length;
      
      if (count !== 4) {
        throw new Error('Each reservation must contain exactly 4 photos.');
      }

      // Prepare metadata for each picture in the batch
      const picturesMetadata = filesData.map(({ file, metadata }) => {
        const pictureMetadata = {
          filename: metadata?.filename || file.name,
          size: metadata?.size || file.size,
          type: metadata?.type || file.type,
          lastModified: metadata?.lastModified,
          lastModifiedDate: metadata?.lastModifiedDate
        };

        // Add EXIF metadata if available
        if (metadata?.shootingTime) {
          pictureMetadata.shootingTime = metadata.shootingTime;
        }
        if (metadata?.cameraMake) {
          pictureMetadata.cameraMake = metadata.cameraMake;
        }
        if (metadata?.cameraModel) {
          pictureMetadata.cameraModel = metadata.cameraModel;
        }
        if (metadata?.gpsLatitude) {
          pictureMetadata.gpsLatitude = metadata.gpsLatitude;
        }
        if (metadata?.gpsLongitude) {
          pictureMetadata.gpsLongitude = metadata.gpsLongitude;
        }
        if (metadata?.orientation) {
          pictureMetadata.orientation = metadata.orientation;
        }
        if (metadata?.exposureTime) {
          pictureMetadata.exposureTime = metadata.exposureTime;
        }
        if (metadata?.fNumber) {
          pictureMetadata.fNumber = metadata.fNumber;
        }
        if (metadata?.iso) {
          pictureMetadata.iso = metadata.iso;
        }

        return pictureMetadata;
      });

      // Step 1: Initiate upload to get SAS URLs (send metadata for all pictures)
      const initiateResponse = await this.initiateUpload(
        count,
        userId,
        picturesMetadata,
        1,
        vehicleProfileId
      );

      const { session_id, sas_urls } = initiateResponse;

      if (typeof session_id !== 'string' || !sas_urls || sas_urls.length !== count ||
        (vehicleProfileId && initiateResponse.vehicle_profile_id !== vehicleProfileId)) {
        throw new Error('Invalid SAS URLs received from server');
      }

      // Step 2: Upload all files to Azure Storage in parallel
      let uploadError = null;

      try {
        const uploadPromises = filesData.map(({ file, onProgress }, index) => {
          const sasInfo = sas_urls.find(s => s.index === index);
          if (!sasInfo) {
            throw new Error(`SAS URL not found for file at index ${index}`);
          }
          return this.uploadToSasUrl(sasInfo.sas_url, file, onProgress);
        });

        // Wait for all uploads to complete (will throw if any fails)
        await Promise.all(uploadPromises);
        
      } catch (error) {
        // Any upload failed
        uploadError = error;
      }

      // Step 3: Confirm upload status (success or failed)
      if (uploadError) {
        await this.confirmUpload(session_id, 'failed', vehicleProfileId);
        throw uploadError;
      }
      return this.confirmPhotoUpload(session_id, vehicleProfileId);
    }

    // Get all pictures
    async getVehicleProfiles() {
      return this.request('/api/pictures/list', {
        method: 'GET',
      });
    }

    // Get upload sessions by time range
    async getUploadSessionsByTimeRange(startTime, endTime) {
      const params = new URLSearchParams({
        start_time: startTime,
        end_time: endTime
      });
      return this.request(`/api/sessions/by-time-range?${params.toString()}`, {
        method: 'GET',
      });
    }

    // Update vehicle profile download status
    async updateVehicleProfileDownloadStatus(vehicleProfileId, downloadStatus) {
      return this.request(`/api/vehicle-profiles/${encodeURIComponent(vehicleProfileId)}/download-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ download_status: downloadStatus }),
      });
    }

    // Update vehicle profile information
    async updateVehicleProfile(vehicleProfileId, profileData) {
      return this.request(`/api/vehicle-profiles/${encodeURIComponent(vehicleProfileId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });
    }

    // Download picture with server-side updated EXIF metadata (Option A)
    getPictureDownloadUrl(pictureId) {
      return this.buildUrl(`/api/pictures/${encodeURIComponent(pictureId)}/download`);
    }

    async downloadPictureBlob(pictureId, options = {}) {
      return this.request(`/api/pictures/${encodeURIComponent(pictureId)}/download`, {
        method: 'GET',
        asBlob: true,
        ...options,
      });
    }

  }
  
  // Create and export a singleton instance
  const apiClient = new ApiClient();
  
  export default apiClient; 